import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { formatBRL, cn } from "@/lib/utils";
import type { StaleLead } from "@/data/types";
import { ThemeToggle } from "@/components/ThemeToggle";

type SortKey = "daysStale" | "amountAtRisk" | "name";

export function LeadsParados({ onBack }: { onBack?: () => void }) {
  const [sortBy, setSortBy] = useState<SortKey>("daysStale");
  const [stepFilter, setStepFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const query = useQuery({
    queryKey: ["stale-leads"],
    queryFn: () => client.getStaleLeads({ limit: 200 }),
  });

  const leads = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  const steps = useMemo(() => {
    const set = new Set<string>();
    leads.forEach((l) => set.add(l.currentStepName));
    return ["all", ...Array.from(set)];
  }, [leads]);

  const visible = useMemo(() => {
    const filtered = leads.filter((l) => {
      if (stepFilter !== "all" && l.currentStepName !== stepFilter) return false;
      if (search && !l.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "amountAtRisk") return b.amountAtRisk - a.amountAtRisk;
      return b.daysStale - a.daysStale;
    });
  }, [leads, stepFilter, search, sortBy]);

  const totalAmountAtRisk = useMemo(
    () => visible.reduce((acc, l) => acc + l.amountAtRisk, 0),
    [visible]
  );

  return (
    <div className="min-h-screen bg-bg-primary px-12 py-10 space-y-8">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-amber" />
          <span className="text-sm font-bold tracking-[0.2em] text-text-primary">HORIZON</span>
          <span className="text-sm text-text-muted">·</span>
          <span className="text-sm font-medium text-text-secondary">Clínica de Radiologia Odontológica</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 rounded-full border border-border-subtle bg-bg-card px-3.5 py-2 text-sm font-medium text-text-secondary transition hover:border-border-strong hover:text-text-primary"
          >
            <span>←</span>
            <span>Voltar ao Dashboard</span>
          </button>
        </div>
      </div>

      {/* Breadcrumb + title */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <button
            onClick={onBack}
            className="transition hover:text-text-secondary"
            type="button"
          >
            Dashboard Executivo
          </button>
          <span>/</span>
          <span className="text-text-secondary">Leads Parados</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-text-primary">Leads Parados</h1>
        <p className="text-[15px] text-text-secondary">
          Oportunidades estagnadas há mais de 30 dias · priorize pela combinação de valor em risco e tempo inativo
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-5">
        <SummaryCard label="Leads parados" value={total.toString()} caption="acima de 30 dias sem interação" />
        <SummaryCard
          label="Valor total em risco"
          value={formatBRL(totalAmountAtRisk)}
          caption={`distribuído em ${visible.length} oportunidades visíveis`}
        />
        <SummaryCard
          label="Tempo médio parado"
          value={
            visible.length
              ? `${Math.round(visible.reduce((a, l) => a + l.daysStale, 0) / visible.length)} dias`
              : "—"
          }
          caption="desde a última interação registrada"
        />
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle bg-bg-card p-4">
        <input
          type="search"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Etapa:</span>
          <select
            value={stepFilter}
            onChange={(e) => setStepFilter(e.target.value)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {steps.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "Todas" : s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Ordenar:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="daysStale">Dias parados</option>
            <option value="amountAtRisk">Valor em risco</option>
            <option value="name">Nome A–Z</option>
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {total} leads
        </div>
      </div>

      {/* Table */}
      <section className="rounded-lg border border-border-subtle bg-bg-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-bg-secondary">
            <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
              <th className="px-5 py-3 text-left">Contato</th>
              <th className="px-5 py-3 text-left">Tags</th>
              <th className="px-5 py-3 text-left">Etapa atual</th>
              <th className="px-5 py-3 text-right">Valor em risco</th>
              <th className="px-5 py-3 text-right">Dias parados</th>
              <th className="px-5 py-3 text-left">Responsável</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-muted">
                  Carregando leads…
                </td>
              </tr>
            )}
            {!query.isLoading && visible.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-muted">
                  Nenhum lead encontrado com os filtros atuais.
                </td>
              </tr>
            )}
            {visible.map((lead, idx) => (
              <LeadRow key={lead.contactId} lead={lead} isLast={idx === visible.length - 1} />
            ))}
          </tbody>
        </table>
      </section>

      <footer className="pt-4 text-center text-xs text-text-muted">
        Horizon Consultoria · CRM artesanal · demo preview
      </footer>
    </div>
  );
}

function LeadRow({ lead, isLast }: { lead: StaleLead; isLast: boolean }) {
  const severity = lead.daysStale >= 90 ? "danger" : lead.daysStale >= 60 ? "warning" : "info";
  const severityChip = {
    danger: "bg-danger/15 text-danger",
    warning: "bg-warning/15 text-warning",
    info: "bg-chart-3/15 text-chart-3",
  }[severity];
  const phoneFmt = lead.number.startsWith("55")
    ? `+55 (${lead.number.slice(2, 4)}) ${lead.number.slice(4, 9)}-${lead.number.slice(9)}`
    : lead.number;
  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-4">
        <div className="font-medium text-text-primary">{lead.name}</div>
        <div className="text-xs text-text-muted">{phoneFmt}</div>
      </td>
      <td className="px-5 py-4">
        <div className="flex flex-wrap gap-1.5">
          {lead.tags.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-bg-primary px-2 py-0.5 text-[11px] font-medium text-text-secondary"
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
              {t.name}
            </span>
          ))}
        </div>
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex items-center gap-2 text-sm text-text-secondary">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: lead.currentStepColor }} />
          {lead.currentStepName}
        </span>
      </td>
      <td className="px-5 py-4 text-right font-semibold text-text-primary">{formatBRL(lead.amountAtRisk)}</td>
      <td className="px-5 py-4 text-right">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", severityChip)}>
          {lead.daysStale}d
        </span>
      </td>
      <td className="px-5 py-4 text-text-secondary">{lead.assignedUserName ?? "—"}</td>
    </tr>
  );
}

function SummaryCard({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-3">
      <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">{label}</span>
      <div className="text-3xl font-bold tracking-tight text-text-primary">{value}</div>
      <p className="text-[13px] text-text-muted">{caption}</p>
    </div>
  );
}
